/**
 * Formulaire de connexion de la v1 (screens/Account/Auth, components/Auth/Firebase/Login.tsx) réutilisé TEL QUEL par la
 * v2 (flag éteint : rien ne change). Retouches du DOM rendu, sous la v2 seulement (revue du 04/10) :
 * - « Mot de passe oublié? » est un lien Chakra sans href (onClick seul) : jamais atteint au clavier. Il reçoit
 *   l'adresse de sa route (#/account/reset-password ; le clic garde le onClick de la v1) et l'espace fine insécable
 *   avant « ? » ;
 * - onglet « S'inscrire » : apostrophe typographique ;
 * - panneaux d'onglets (TabPanel, tabIndex 0) : retirés de l'ordre de tabulation (ils contiennent des champs ; le
 *   panneau entier prenait le focus avec un grand contour qui mordait l'onglet actif) ;
 * - messages d'erreur : graphie de la v2 (« e-mail », revue du 05/10), sans toucher aux textes de la v1.
 * React ne réécrit ni ces attributs (absents ou inchangés dans ses props) ni ces textes (statiques) : les retouches
 * tiennent ; un observateur les repose quand un onglet rend un nouveau formulaire.
 */
import { useEffect, useState } from 'react';
import { E_MAIL, E_MAIL_CAP, typo } from '../../lib/typo';

const FORGOT = /^Mot de passe oublié\s*\?$/;

/** Messages d'erreur de la v1 (et réponse de connexion refusée) → libellés de la v2. */
const ERROR_TEXTS: Record<string, string> = {
  'Veuillez entrer un email valide': `Indiquez une adresse ${E_MAIL} valide.`,
  'Veuillez entrer un mot de passe valide': 'Indiquez votre mot de passe.',
  'Login et/ou mot de passe invalide(s)': `${E_MAIL_CAP} ou mot de passe incorrect.`,
};

/**
 * Message générique de la v1 (« Veuillez entrer une valeur valide », sans point) : précisé selon le champ, retrouvé par
 * le nom de son champ (revue du 05/10 : trois fois le même message imprécis à côté des messages de la v2).
 */
const GENERIC_ERROR = 'Veuillez entrer une valeur valide';
const FIELD_ERRORS: Record<string, string> = {
  FirstName: 'Indiquez votre prénom.',
  LastName: 'Indiquez votre nom.',
  BirthDate: 'Indiquez votre date de naissance.',
  Email: `Indiquez une adresse ${E_MAIL} valide.`,
  Password: 'Indiquez votre mot de passe.',
  MobilePhone: 'Indiquez votre numéro de téléphone.',
};

export function patchV1Auth(root: HTMLElement): void {
  root.querySelectorAll<HTMLAnchorElement>('a.chakra-link:not([href])').forEach((link) => {
    const text = link.textContent?.trim() ?? '';
    if (!FORGOT.test(text)) return;
    link.setAttribute('href', '#/account/reset-password');
    if (link.childNodes.length === 1 && link.firstChild?.nodeType === Node.TEXT_NODE) {
      link.firstChild.textContent = 'Mot de passe oublié ?';
    }
  });
  root.querySelectorAll<HTMLElement>('.chakra-tabs__tab').forEach((tab) => {
    const node = tab.firstChild;
    if (tab.childNodes.length === 1 && node?.nodeType === Node.TEXT_NODE && node.textContent?.includes("'")) {
      node.textContent = node.textContent.replace(/'/g, '’');
    }
  });
  // Libellés de la v1 (« Comment avez-vous entendu parler de nous ? ») : espace insécable avant « ? », apostrophes.
  root.querySelectorAll<HTMLElement>('.chakra-form__label').forEach((label) => {
    label.childNodes.forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE || !node.textContent) return;
      const next = typo(node.textContent);
      if (next !== node.textContent) node.textContent = next;
    });
  });
  root.querySelectorAll<HTMLElement>('.chakra-tabs__tab-panel[tabindex="0"]').forEach((panel) => {
    panel.setAttribute('tabindex', '-1');
  });
  root.querySelectorAll<HTMLElement>('.chakra-form__error-message').forEach((message) => {
    message.childNodes.forEach((node) => {
      const text = node.nodeType === Node.TEXT_NODE ? (node.textContent ?? '').trim() : '';
      let next = ERROR_TEXTS[text];
      if (!next && text === GENERIC_ERROR) {
        const field = message.closest('.chakra-form-control')?.querySelector<HTMLInputElement>('input[name], select[name]');
        next = (field && FIELD_ERRORS[field.name]) || 'Indiquez une valeur valide.';
      }
      if (next && node.textContent !== next) node.textContent = next;
    });
  });
}

/**
 * Renvoie un ref de rappel à poser sur le conteneur du formulaire : les retouches commencent quand il apparaît (la fiche
 * cours ne rend le formulaire qu'une fois le cours chargé).
 */
export function useV1AuthPatches(): (node: HTMLElement | null) => void {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!root) return undefined;
    patchV1Auth(root);
    if (typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(() => patchV1Auth(root));
    // characterData : un message d'erreur qui change de texte (React réécrit le nœud texte).
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [root]);
  return setRoot;
}
